// src/pages/Tarotscope.js
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

/* ===== Shared style tokens ===== */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";

/* Page canvas (turq/plum ribbons) */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.12), rgba(118, 159, 171, 0) 40%),
  linear-gradient(180deg, #014C75 10%, #005C83 50%, #016280ff 30%, #0C4B84 72%, #014C75 90%)
`;

/* Icon glow recipe */
const ICON_GLOW_BASE =
  "w-24 h-24 md:w-24 md:h-24 rounded-lg object-contain " +
  "shadow-[0_2px_4px_rgba(0,0,0,0.45)] " +
  "transition-transform duration-200 group-hover:-translate-y-[1px] " +
  "pointer-events-none select-none";

const ICON_GLOW_HOV = "group-hover:shadow-[0_16px_36px_rgba(88,64,120,0.55)]";

/* Where your zodiac icons live */
const ICON_BASE = "/icons";

/* Signs with date ranges */
const SIGNS = [
  { name: "Aries", slug: "aries", dates: "Mar 21 – Apr 19" },
  { name: "Taurus", slug: "taurus", dates: "Apr 20 – May 20" },
  { name: "Gemini", slug: "gemini", dates: "May 21 – Jun 20" },
  { name: "Cancer", slug: "cancer", dates: "Jun 21 – Jul 22" },
  { name: "Leo", slug: "leo", dates: "Jul 23 – Aug 22" },
  { name: "Virgo", slug: "virgo", dates: "Aug 23 – Sep 22" },
  { name: "Libra", slug: "libra", dates: "Sep 23 – Oct 22" },
  { name: "Scorpio", slug: "scorpio", dates: "Oct 23 – Nov 21" },
  { name: "Sagittarius", slug: "sagittarius", dates: "Nov 22 – Dec 21" },
  { name: "Capricorn", slug: "capricorn", dates: "Dec 22 – Jan 19" },
  { name: "Aquarius", slug: "aquarius", dates: "Jan 20 – Feb 18" },
  { name: "Pisces", slug: "pisces", dates: "Feb 19 – Mar 20" },
];

export default function Tarotscope() {
  return (
    <div
      className={`${FONT} min-h-screen relative overflow-hidden text-white`}
      style={{ background: CANVAS_BG }}
    >
      <Helmet>
        <title>Daily Tarotscope | TarotFate</title>
        <meta
          name="description"
          content="Pick your sign to read today’s tarotscope. Astrology sets the mood; a Tarot card adds a clear theme you can act on today."
        />
        <link rel="canonical" href="https://tarotfate.com/tarotscope" />
      </Helmet>

      {/* ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* Header */}
      <header className={`${WRAP} ${MAXW} pt-8 pb-4 text-center relative z-[1]`}>
        <div className="flex justify-center">
          <img
            src="/icons/tarotscope.webp"
            alt="Tarotscope"
            className="w-32 h-auto sm:w-36 md:w-40 lg:w-44 object-contain mx-auto select-none pointer-events-none"
            onContextMenu={(e) => e.preventDefault()}
            draggable="false"
          />
        </div>
        <div className="flex items-center justify-center mt-2 mb-2" aria-hidden>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          <span className="px-4 text-white/90">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
        <p className={`${TEXT} max-w-3xl mx-auto leading-relaxed text-base`}>
          Pick your sign to view today’s message. Astrology frames the mood and timing.
          A Tarot card adds a clear theme you can use now. Notice what to lean into,
          what to soften, and where one brave step could shift your day.
        </p>
      </header>

      {/* Grid */}
      <main className={`${WRAP} ${MAXW} pb-16 relative z-[1]`}>
        <div
          className="
            grid gap-4
            grid-cols-2
            md:grid-cols-4
            xl:grid-cols-4
          "
          role="list"
          aria-label="Select your zodiac sign"
        >
          {SIGNS.map((s) => {
            const img = `${ICON_BASE}/tarotscope_${s.slug}.webp`;
            const alt = `${s.name} tarotscope icon`;

            return (
              <Link
                to={`/tarotscope/${s.slug}`}
                key={s.slug}
                className="
                  group rounded-none select-none focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f3d3f]
                  transition-all duration-150 hover:-translate-y-1
                  text-[#3f2d61] backdrop-blur-md
                  bg-gradient-to-b from-white/3 via-[#14656a]/35 to-[#0f3d3f]/12
                  [background-clip:padding-box,border-box]
                  shadow-[0_0px_0px_0_rgba(0,0,0,0.8)]
                  p-4 text-center
                "
                role="listitem"
                aria-label={`${s.name} tarotscope`}
              >
                {/* Icon + glow */}
                <div className="flex justify-center pt-1">
                  <div className="relative">
                    <img
                      src={img}
                      alt={alt}
                      width={96}
                      height={96}
                      loading="lazy"
                      decoding="async"
                      draggable="false"
                      className={`${ICON_GLOW_BASE} ${ICON_GLOW_HOV}`}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                    {/* hover bloom */}
                    <span
                      aria-hidden
                      className="
                        absolute inset-0 rounded-lg opacity-0
                        group-hover:opacity-100 transition duration-200 ease-out
                        blur-[16px]
                        bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.55),transparent_70%)]
                        pointer-events-none
                      "
                    />
                  </div>
                </div>

                {/* Labels */}
                <div className="flex flex-col items-center text-center px-3 pb-1 mt-2">
                  <h3 className="text-base font-semibold text-white tracking-wide">
                    {s.name}
                  </h3>
                  <p className="mt-1 text-[14px] text-white/80 leading-snug">
                    {s.dates}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

       
         
      </main>
    </div>
  );
}
